/* Hub consolidation pattern -- shared components.
 *
 * The FDE consolidation (Phase 20-tail sprint item #3) pilots the pattern
 * that Admin / HR / Sales / Service hubs will re-use verbatim:
 *   1. HubShell        -- top page-header + adaptive sub-nav host
 *   2. HubSubNav       -- vertical rail on >=1024px, horizontal tabs otherwise
 *   3. HubRedirect     -- <Navigate replace> that preserves path params +
 *                          query string, and fires a one-shot per-URL toast
 *                          the first time each legacy route is used.
 */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import GlassSurface from "@/components/ui/GlassSurface";
import { PageHeader } from "@/components/PageHeader";

/**
 * Sub-nav rail / tab bar. Renders vertically on >=1024px, horizontally
 * (scroll-snap) below that. Two shapes:
 *
 *   items:  [{key, label, to, icon: Icon, badge?, end?}]                (flat)
 *   groups: [{title, items: [...]}, ...]                                (grouped -- Admin hub)
 *
 * Grouped mode adds collapsible section headers on the desktop rail and a
 * flat concatenation on the mobile tab strip (grouping is preserved via
 * subtle group-label chips).
 */
export function HubSubNav({ items = [], groups = null, className = "", testid = "hub-subnav" }) {
  const isGrouped = Array.isArray(groups) && groups.length > 0;
  const location = useLocation();
  const flatItems = useMemo(
    () => (isGrouped ? groups.flatMap((g) => g.items || []) : items),
    [isGrouped, groups, items],
  );

  // Auto-expand the group whose item matches the current URL. Persist manual
  // open/close per-group in localStorage.
  const groupKeys = isGrouped ? groups.map((g) => g.key || g.title) : [];
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("hub_subnav_openGroups") || "null");
      if (stored && typeof stored === "object") return stored;
    } catch (_e) { /* ignore */ }
    return Object.fromEntries(groupKeys.map((k) => [k, true]));
  });

  useEffect(() => {
    if (!isGrouped) return;
    // Auto-open the group containing the active path if it's currently closed.
    const activeGroup = groups.find((g) =>
      (g.items || []).some((it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/")),
    );
    if (activeGroup) {
      const key = activeGroup.key || activeGroup.title;
      setOpenGroups((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
    }
  }, [location.pathname, isGrouped, groups]);

  useEffect(() => {
    try { localStorage.setItem("hub_subnav_openGroups", JSON.stringify(openGroups)); } catch (_e) { /* ignore */ }
  }, [openGroups]);

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const renderItem = (it) => {
    const Icon = it.icon;
    return (
      <NavLink
        key={it.key || it.to}
        to={it.to}
        end={it.end}
        data-testid={`${testid}-item-${it.key || it.to}`}
        className={({ isActive }) =>
          [
            "flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm transition-colors",
            isActive
              ? "bg-sky-500/15 text-sky-200 border border-sky-500/30"
              : "text-foreground/75 hover:bg-white/5 hover:text-foreground border border-transparent",
          ].join(" ")
        }
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />}
        <span className="flex-1 truncate">{it.label}</span>
        {it.badge != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/8 text-foreground/60 tabular-nums">
            {it.badge}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Desktop vertical rail */}
      <aside
        className={`hidden lg:block sticky top-4 w-[240px] shrink-0 ${className}`}
        data-testid={`${testid}-rail`}
      >
        <GlassSurface variant="card" className="p-2 max-h-[calc(var(--vh,1vh)*100-6rem)] overflow-y-auto">
          {isGrouped ? (
            <nav className="space-y-2">
              {groups.map((g) => {
                const gKey = g.key || g.title;
                const isOpen = openGroups[gKey] !== false;
                return (
                  <div key={gKey} className="space-y-0.5">
                    <button
                      onClick={() => toggleGroup(gKey)}
                      data-testid={`${testid}-group-${gKey}`}
                      className="w-full flex items-center gap-1.5 px-2 pt-2 pb-1 text-[9.5px] uppercase tracking-[0.16em] text-foreground/50 hover:text-foreground/75 transition"
                    >
                      <ChevronRight
                        className={`h-3 w-3 transition-transform ${isOpen ? "rotate-90" : ""}`}
                      />
                      <span className="flex-1 text-left truncate">{g.title}</span>
                      <span className="text-[9px] text-foreground/35 tabular-nums">{(g.items || []).length}</span>
                    </button>
                    {isOpen && <div className="space-y-0.5 pl-1">{(g.items || []).map(renderItem)}</div>}
                  </div>
                );
              })}
            </nav>
          ) : (
            <nav className="space-y-0.5">{items.map(renderItem)}</nav>
          )}
        </GlassSurface>
      </aside>

      {/* Tablet / mobile horizontal tab strip */}
      <div
        className={`lg:hidden -mx-2 px-2 overflow-x-auto scrollbar-none ${className}`}
        data-testid={`${testid}-tabs`}
      >
        <div className="flex gap-2 py-2 min-w-max">
          {isGrouped
            ? groups.flatMap((g) =>
                (g.items || []).map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.key || it.to}
                      to={it.to}
                      end={it.end}
                      data-testid={`${testid}-tab-${it.key || it.to}`}
                      className={({ isActive }) =>
                        [
                          "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap border",
                          isActive
                            ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                            : "text-foreground/70 border-white/8 hover:bg-white/5",
                        ].join(" ")
                      }
                    >
                      {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                      <span>{it.label}</span>
                    </NavLink>
                  );
                }),
              )
            : items.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.key || it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={`${testid}-tab-${it.key || it.to}`}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors whitespace-nowrap border",
                        isActive
                          ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                          : "text-foreground/70 border-white/8 hover:bg-white/5",
                      ].join(" ")
                    }
                  >
                    {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />}
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
          {/* Suppress unused-var lint from useMemo (flatItems reserved for
              future keyboard-nav helpers; kept to preserve public API). */}
          {flatItems.length === -1 && null}
        </div>
      </div>
    </>
  );
}

/**
 * Full hub shell with header + adaptive sub-nav rail. Sub-page content is
 * rendered as children (typically a `<Outlet />` from react-router).
 */
export function HubShell({ title, subtitle, actions, subNav, children, testid = "hub" }) {
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <PageHeader title={title} subtitle={subtitle} />
        </div>
        {actions}
      </div>

      {/* D15 P0 fix -- mobile: stack subNav strip ABOVE content so
          `flex-1 min-w-0` content actually receives width. Desktop
          (>=lg) keeps the classic side-rail layout. Before this fix
          the horizontal `hub-subnav-tabs` strip consumed the whole
          flex row and squeezed content to 0-18px. Regression
          contract locked in
          `tests/test_dispatch15_hubshell_mobile_flex.py`. */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {subNav}
        <div className="flex-1 min-w-0 w-full" data-testid={`${testid}-content`}>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * 6b -- one-shot toast per legacy URL. localStorage flag prevents
 * re-firing on refresh.
 */
const REDIRECT_TOAST_FLAG_PREFIX = "hub_redirect_toast_seen:";

/**
 * `<HubRedirect from="/visitors" to="/front-desk/visitors" message="..." />`
 *
 * - Preserves path params via `*` splat and useParams.
 * - Preserves query string + hash.
 * - Fires a one-shot toast the first time each user hits this legacy path.
 */
export function HubRedirect({ toBase, splatKey = "*", message }) {
  const params = useParams();
  const loc = useLocation();
  const tail = params[splatKey] || "";
  const target = useMemo(() => {
    const path = tail ? `${toBase}/${tail}` : toBase;
    return `${path}${loc.search || ""}${loc.hash || ""}`;
  }, [toBase, tail, loc.search, loc.hash]);

  useEffect(() => {
    if (!message) return;
    const flag = `${REDIRECT_TOAST_FLAG_PREFIX}${toBase}`;
    try {
      if (localStorage.getItem(flag)) return;
      localStorage.setItem(flag, "1");
      // Fire once we're actually navigating; sonner is safe pre-mount.
      setTimeout(() => toast.info(message, { duration: 5000 }), 100);
    } catch (_e) { /* localStorage blocked -- skip */ }
  }, [toBase, message]);

  return <Navigate to={target} replace />;
}

export default HubShell;
